import React, { startTransition, useEffect, useRef, useSyncExternalStore } from 'react';
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import styled, { css } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { easing, reducedMotion } from '../styles/motion';
import { pillTrack, pillSegment } from '../styles/buttons'; // [BUTTON-STYLE 2026-09-30]

/**
 * [RD-PILL 2026-09-10] Refunds | Disputes pill, sitting where each page's <h1> title used to.
 * It replaced a title dropdown (click "Refunds", then pick "Disputes"): both views are now on
 * show and one click swaps them. Same segmented recipe as the EOM / Dashboard toggle (EOMPage)
 * so it reads as the app's own control.
 *
 * The highlighted half follows the ROUTE, not the page rendering the pill. RefundsDisputesPage
 * keeps BOTH pages mounted — each with its own copy of this pill — and slides between them, so
 * both copies must move their thumb together; that is what lets the toolbar swap copies at the
 * start of the slide with no visible jump. Picking a view just navigates; nothing remounts.
 *
 * Permission-aware: a user who can only see one of the two gets a plain (non-interactive) title.
 */

export type SectionView = 'refunds' | 'disputes';

const VIEWS: { key: SectionView; label: string; path: string }[] = [
  { key: 'refunds', label: 'Refunds', path: '/refunds' },
  { key: 'disputes', label: 'Disputes', path: '/disputes' },
];

/** Which view the URL is on — shared with RefundsDisputesPage so the pill and the slide agree. */
export function viewFromPath(pathname: string): SectionView {
  return /^\/disputes(\/|$)/.test(pathname) ? 'disputes' : 'refunds';
}

const PlainTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;
`;

/* [BUTTON-STYLE 2026-09-30] Track and segment sizes come from styles/buttons (pillTrack / pillSegment). */
const Segmented = styled.div<{ $liquid?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  flex-shrink: 0; /* the search box beside it gives way, never the pill */
  ${pillTrack}
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.05)')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)')};
  /* [LIQUID-GLASS 2026-11-02, user] the liquid prop (Refunds / Disputes): the track pressed into the glass */
  ${(p: any) => p.$liquid && css`
    background: ${p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)'};
    border-color: transparent;
    box-shadow: ${p.theme.colorScheme === 'dark'
      ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
      : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.08)'};
  `}
`;

const SegThumb = styled.div<{ $right: boolean; $liquid?: boolean }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  width: calc((100% - 6px) / 2);
  border-radius: 999px;
  background: linear-gradient(180deg, ${(p) => p.theme.green600}, ${(p) => p.theme.green700});
  box-shadow: 0 2px 8px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(11,102,64,0.32)' : 'rgba(20, 107, 84, 0.32)')};
  transform: translateX(${(p) => (p.$right ? '100%' : '0')});
  transition: transform 0.26s ${easing.emphasized};
  /* [LIQUID-GLASS 2026-11-02] liquid: glossy (a shine + a light top edge) and the theme switch's slide (0.42 s, the same curve) */
  ${(p) => p.$liquid && css`
    background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%),
      linear-gradient(180deg, ${p.theme.green600}, ${p.theme.green700});
    box-shadow: 0 2px 8px ${p.theme.colorScheme === 'dark' ? 'rgba(11,102,64,0.32)' : 'rgba(20, 107, 84, 0.32)'}, inset 0 1px 0 rgba(255,255,255,0.45);
    transition: transform 0.42s cubic-bezier(0.32, 0.72, 0, 1);
  `}
  ${reducedMotion} { transition: none; }
`;

const Seg = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  ${pillSegment}
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${(p) => (p.$active ? '#fff' : p.theme.muted)};
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover { color: ${(p) => (p.$active ? '#fff' : p.theme.colorScheme === 'dark' ? '#fff' : p.theme.text)}; }
`;

/* [FLUID-6 2026-10-12, user] "it feels laggy": the thumb used to move only once the route change had re-rendered BOTH
   pages (~0.1 s on a dev build, longer on a busy PC), so the pill lagged the click. Now the view just picked is kept in
   this tiny store: both pill copies (one per page) move their thumbs at the click itself, together, and the page change
   follows as a low-priority update that can never hold the thumb back. Cleared once the route has caught up. */
let pickedView: SectionView | null = null;
const pickListeners = new Set<() => void>();
const setPickedView = (v: SectionView | null) => { pickedView = v; pickListeners.forEach((l) => l()); };
const subscribePicked = (l: () => void) => { pickListeners.add(l); return () => { pickListeners.delete(l); }; };
const readPicked = () => pickedView;

/** liquid = [LIQUID-GLASS 2026-11-02] the glass switch (Refunds / Disputes); Inventory keeps the plain one. */
const SectionSwitcher: React.FC<{ current: SectionView; liquid?: boolean }> = ({ current, liquid = false }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const routeView = viewFromPath(pathname);
  const picked = useSyncExternalStore(subscribePicked, readPicked, readPicked);
  // Any route change (the one picked, or any other) hands the thumb back to the route — it can never stick.
  useEffect(() => { setPickedView(null); }, [routeView]);
  // [FLUID-8 2026-10-12, user] The pill switches on the mouse PRESS (the click only comes on release); touch / keyboard click.
  const pressedRef = useRef(false);
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const allowed = new Set(user?.allowed_sections || []);
  // Only offer the views this user is actually permitted to open.
  const options = VIEWS.filter((v) => isAdmin || allowed.has(v.key));

  // Nothing to switch between → render a plain, non-interactive heading.
  if (options.length <= 1) {
    return <PlainTitle>{VIEWS.find((v) => v.key === current)?.label ?? ''}</PlainTitle>;
  }

  const active = picked ?? routeView;
  const go = (v: { key: SectionView; path: string }) => {
    if (v.key === active) return;
    setPickedView(v.key); // [FLUID-6 2026-10-12] the thumbs move now…
    startTransition(() => navigate(v.path)); // …and the page change follows without holding them back
  };

  return (
    <Segmented role="tablist" aria-label="Refunds or Disputes" $liquid={liquid}>
      <SegThumb $right={active === 'disputes'} $liquid={liquid} aria-hidden="true" />
      {VIEWS.map((v) => (
        <Seg
          key={v.key}
          type="button"
          role="tab"
          aria-selected={active === v.key}
          $active={active === v.key}
          onPointerDown={(e) => {
            pressedRef.current = isPlainMousePress(e);
            if (pressedRef.current) go(v);
          }}
          onClick={(e) => {
            if (clickEndsPress(pressedRef, e)) return; // the press already switched
            go(v);
          }}
        >
          {v.label}
        </Seg>
      ))}
    </Segmented>
  );
};

export default SectionSwitcher;
